import { useState } from 'react';
import { Brain, Plus, Trash2 } from 'lucide-react';
import {
  EmptyState,
  GlassCard,
  Modal,
  SettingsPageHeader,
  SurfacePageBody,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import {
  SEED_SETTINGS_MEMORIES,
  type SettingsMemory,
} from '@/versions/v1/shared/settings-seed';
import { formatRelativeTime } from './format-utils';

export function MemoryScene() {
  const [memories, setMemories] = useState<SettingsMemory[]>(
    () => [...SEED_SETTINGS_MEMORIES].reverse(),
  );
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState('');

  const handleCreate = () => {
    const content = draft.trim();
    if (!content) return;
    const entry: SettingsMemory = {
      id: `mem-${Date.now()}`,
      content,
      createdAt: Date.now(),
      lastReadAt: Date.now(),
      type: 'user',
    };
    setMemories((prev) => [entry, ...prev]);
    setDraft('');
    setCreateOpen(false);
  };

  const handleDelete = (id: string) => {
    setMemories((prev) => prev.filter((m) => m.id !== id));
  };

  return (
    <SurfacePageLayout width="narrow">
      <Commentable name="MemoryScene">
        <div className="contents">
          <SurfacePageBody className="space-y-8" as="div">
          <SettingsPageHeader
            title="Memory"
            description="Facts the agent remembers about you across conversations."
            actions={
              <button
                type="button"
                onClick={() => setCreateOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-accent-500 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-accent-600 dark:bg-accent-500 dark:text-white dark:hover:bg-accent-400"
              >
                <Plus className="h-4 w-4" />
                New memory
              </button>
            }
          />

          <Commentable name="MemoryList">
            <section>
              <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-muted">
                Saved
              </h2>
              {memories.length === 0 ? (
                <EmptyState
                  icon={<Brain className="h-8 w-8 text-zinc-400" />}
                  title="Nothing saved yet"
                  description="The agent hasn't been told anything yet. Add a memory above, or ask the agent to remember something during a chat."
                />
              ) : (
                <GlassCard className="overflow-hidden">
                  {memories.map((m, i) => (
                    <div
                      key={m.id}
                      className={`flex items-start gap-3 px-5 py-4 ${
                        i > 0 ? 'border-t border-[var(--glass-border)]' : ''
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="whitespace-pre-wrap break-words text-sm text-zinc-900 dark:text-zinc-100">
                          {m.content}
                        </p>
                        <p className="mt-1 text-xs text-zinc-400 dark:text-zinc-500">
                          {formatRelativeTime(m.createdAt)}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDelete(m.id)}
                        className="flex-shrink-0 rounded-md p-1.5 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-red-600 dark:hover:bg-zinc-800 dark:hover:text-red-400"
                        aria-label="Delete memory"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </GlassCard>
              )}
            </section>
          </Commentable>

          <Modal open={createOpen} onOpenChange={setCreateOpen} title="Add memory">
            <div className="space-y-4 px-6 pb-6">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={4}
                placeholder="Something the agent should remember…"
                className="w-full rounded-lg border border-divider bg-input px-3 py-2 text-sm outline-none focus:border-input-focus"
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setCreateOpen(false)}
                  className="rounded-lg border border-divider px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 dark:text-zinc-300 dark:hover:bg-zinc-900"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreate}
                  className="rounded-lg bg-zinc-900 px-3 py-2 text-sm font-medium text-white hover:bg-zinc-800 dark:bg-zinc-100 dark:text-zinc-900"
                >
                  Save
                </button>
              </div>
            </div>
          </Modal>
          </SurfacePageBody>
        </div>
      </Commentable>
    </SurfacePageLayout>
  );
}
